import { Loader2 } from 'lucide-react';
import React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { usePrivateNotes } from '@/features/projects/project-details/hooks/use-private-notes';
import { useTeamNotes } from '@/features/projects/project-details/hooks/use-team-notes';
import { Tabs, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { Project } from '@/shared/types/api';
import { cn } from '@/shared/utils/cn';

export const ProjectNotes = ({
  project,
  noteType: initialNoteType,
  onNoteTypeChange,
  isPropertiesExpanded = false,
}: {
  project: Project;
  noteType: 'team' | 'private';
  onNoteTypeChange?: (type: 'team' | 'private') => void;
  isPropertiesExpanded?: boolean;
}) => {
  const { t } = useTranslation('project', { keyPrefix: 'Shared' });
  const teamNotes = useTeamNotes(project.id);
  const privateNotes = usePrivateNotes(project.id);
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const [noteType, setNoteType] = useState<'team' | 'private'>(initialNoteType);

  // Get content based on note type
  const currentContent =
    noteType === 'team' ? teamNotes.data : privateNotes.data;

  const handleNoteTypeChange = (value: string) => {
    const newType = value as 'team' | 'private';
    setNoteType(newType);
    onNoteTypeChange?.(newType);
  };

  const [description, setDescription] = useState(currentContent);
  const [isEditing, setIsEditing] = useState(false);
  const [hasChanged, setHasChanged] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Update local state when switching note types or when data changes
  React.useEffect(() => {
    setDescription(currentContent);
    setHasChanged(false);
  }, [currentContent, noteType]);

  React.useEffect(() => {
    if (!hasChanged) return;
    const handler = setTimeout(() => {
      setIsSaving(true);
      if (noteType === 'team') {
        if (description !== teamNotes.data) {
          teamNotes.mutation.mutate(
            { content: description },
            {
              onSettled: () => setIsSaving(false),
            },
          );
        }
      } else {
        if (description !== privateNotes.data) {
          privateNotes.mutation.mutate(
            { content: description },
            {
              onSettled: () => setIsSaving(false),
            },
          );
        }
      }
      setHasChanged(false);
    }, 800);
    return () => clearTimeout(handler);
  }, [description, hasChanged, noteType, teamNotes, privateNotes]);

  React.useEffect(() => {
    if (isEditing) textareaRef.current?.focus();
  }, [isEditing]);

  return (
    <div
      className={cn(
        'relative w-full overflow-hidden rounded-[12px] p-px',
        isPropertiesExpanded ? 'h-full min-h-[172px]' : 'h-full min-h-[300px]',
      )}
      style={{
        background:
          'linear-gradient(to bottom, rgba(255,255,255,0) 12.871%, rgba(255,255,255,0.06) 87.632%)',
      }}
    >
      <div className="relative flex h-full flex-col rounded-[12px] bg-transparent p-4 shadow-[0px_20px_50px_0px_inset_rgba(255,255,255,0.01)]">
        <Tabs value={noteType} onValueChange={handleNoteTypeChange}>
          <TabsList
            className={cn(
              'mb-6 flex h-9 gap-6 rounded-[8px] border-none bg-[#2d2a3c] p-1',
              isPropertiesExpanded ? 'w-full' : 'w-full',
            )}
          >
            <TabsTrigger
              value="team"
              className={cn(
                'flex h-full grow basis-0 items-center justify-center px-3 py-2.5 text-[13px] font-semibold leading-[18px] text-white',
                noteType === 'team'
                  ? 'rounded-[4px] bg-[#504d5d]'
                  : 'rounded-[14px] bg-transparent',
              )}
            >
              {t('team_notes')}
            </TabsTrigger>
            <TabsTrigger
              value="private"
              className={cn(
                'flex h-full grow basis-0 items-center justify-center px-3 py-2.5 text-[13px] font-semibold leading-[18px] text-white',
                noteType === 'private'
                  ? 'rounded-[4px] bg-[#504d5d]'
                  : 'rounded-[14px] bg-transparent',
              )}
            >
              {t('my_notes')}
            </TabsTrigger>
          </TabsList>
        </Tabs>
        <div
          className="flex flex-1 flex-col"
          onClick={() => textareaRef.current?.focus()}
        >
          <textarea
            ref={textareaRef}
            className={cn(
              'h-full w-full resize-none bg-transparent text-[13px] leading-[19.5px] text-[#FEFEFE] outline-none placeholder:text-white/60',
              isEditing ? '' : 'cursor-pointer',
            )}
            value={description}
            placeholder={
              noteType === 'team'
                ? t('team_note_placeholder')
                : t('private_note_placeholder')
            }
            onChange={(e) => {
              setDescription(e.target.value);
              setHasChanged(true);
            }}
            onClick={() => setIsEditing(true)}
            onBlur={() => {
              // Don't exit editing mode if we're currently saving
              if (!isSaving) {
                setIsEditing(false);
              }
            }}
            rows={4}
          />
        </div>
        {((noteType === 'team' &&
          (teamNotes.mutation.isPending || teamNotes.isLoading)) ||
          (noteType === 'private' &&
            (privateNotes.mutation.isPending || privateNotes.isLoading))) && (
          <span className="absolute right-4 top-4">
            <Loader2 className="animate-spin text-white/80" />
          </span>
        )}
      </div>
    </div>
  );
};
